import { useState, useEffect, useCallback } from "react";
import { useUser } from "../../context/userContext";
import { updateProfile, uploadMyIcPhoto, getMyIcPhoto, deleteMyIcPhoto }
    from "../../api/userApi";
import { formatPhoneNumber } from "../../utils/phoneUtils";
import BankSelect from '../common/bankSelect';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { formatIc, icInput } from '../../utils/icFormat';

// LHDN tax-type codes printed on the CP58 form. The prefix is what
// goes into the small 2-cell box; the rest of the value is the digits.
const TAX_TYPE_OPTIONS = ['', 'C', 'C1', 'TN', 'PT', 'SG', 'OG', 'D', 'TP', 'TJ', 'TF', 'IG'];

// Split a stored tax string ("IG26590393090") into { type: "IG", no: "26590393090" }.
// If the string starts with digits, type is empty.
function splitTaxNo(combined) {
    if (!combined) return { type: '', no: '' };
    const m = String(combined).match(/^([A-Z]{1,2})\s*(\d.*)?$/);
    return m ? { type: m[1], no: (m[2] || '').replace(/\s+/g, '') } : { type: '', no: combined };
}

// Combine for storage. Keeps both halves trimmed.
function combineTaxNo(type, no) {
    const t = (type || '').trim().toUpperCase();
    const n = (no || '').trim();
    if (!t && !n) return '';
    return t + n;
}

// Section header used between Personal / Company / Bank groups so the
// long form is easier to scan.
const SectionHeader = ({ label }) => (
    <Typography
        sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            color: '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: "'Poppins', sans-serif",
            mt: 1,
        }}
    >
        {label}
    </Typography>
);

/**
 * `simple` strips the form down to what is needed to PAY somebody: who they are, their IC
 * and its photocopy, and a bank account. It is passed for a pure Referral, who files no
 * CP58 with us and bills through no company, so the tax and company blocks are two sets of
 * fields they would have to read past on every visit without ever filling one in.
 * (user 2026-10-31: "only need to have some details like name bank acc, ic photo, tax
 *  remove it")
 */
export default function UpdateUserDetailsForm({ userDetails, setUserDetails, simple = false }) {

    /* IC PHOTOCOPY. The number alone cannot be checked against anything, and the CP58 is
       made out against the document - so the profile asks for both. Fetched as a blob
       through an authenticated endpoint rather than an <img src> to a static path,
       because a government ID must not sit behind a guessable URL.
       (user 2026-10-23: "need to have another input to add ic") */
    const [icUrl, setIcUrl] = useState('');
    const [icBusy, setIcBusy] = useState(false);
    const [icErr, setIcErr] = useState('');

    const loadIc = useCallback(async () => {
        try {
            const res = await getMyIcPhoto();
            setIcUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
        } catch {
            setIcUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return ''; });   // none on file yet
        }
    }, []);

    useEffect(() => { loadIc(); }, [loadIc]);
    // Blob URLs are held by the browser until revoked; without this every reload leaks one.
    useEffect(() => () => { if (icUrl) URL.revokeObjectURL(icUrl); }, [icUrl]);

    const onIcPick = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';                      // allow re-picking the same file
        if (!file) return;
        setIcBusy(true); setIcErr('');
        try {
            await uploadMyIcPhoto(file);
            await loadIc();
        } catch (err) {
            const d = err?.response?.data;
            setIcErr(d?.message || (typeof d === 'string' && d.trim()) || 'Could not upload that file.');
        } finally { setIcBusy(false); }
    };

    const onIcRemove = async () => {
        setIcBusy(true); setIcErr('');
        try { await deleteMyIcPhoto(); await loadIc(); }
        catch { setIcErr('Could not remove it.'); }
        finally { setIcBusy(false); }
    };
    const { user } = useUser();
    const [saving, setSaving] = useState(false);

    const handleUpdateUserProfile = async () => {
        setSaving(true);
        try {
            await updateProfile({
                firstName: userDetails.firstName,
                lastName: userDetails.lastName,
                alias: userDetails.alias,
                email: userDetails.email,
                phoneNumber: userDetails.phoneNumber,
                icNumber: formatIc(userDetails.icNumber),
                incomeTaxNo: userDetails.incomeTaxNo,
                bankName: userDetails.bankName,
                bankAccountNumber: userDetails.bankAccountNumber,
                bankBeneficiaryName: userDetails.bankBeneficiaryName,
                billingName: userDetails.billingName,
                companyRegNo: userDetails.companyRegNo,
                companyIncomeTaxNo: userDetails.companyIncomeTaxNo,
                companyAddress: userDetails.companyAddress,
                address: userDetails.address,
            });
            alert('Profile updated successfully');
        } catch (err) {
            alert('Failed to update: ' + (err.response?.data || err.message));
        } finally {
            setSaving(false);
        }
    };

    const set = (k) => (e) => setUserDetails({ ...userDetails, [k]: e.target.value });

    // Local split state for the two-input tax fields, mirrored into
    // userDetails on every change so the saved value stays a single
    // string ("IG26590393090") matching the DB column.
    const personalTax = splitTaxNo(userDetails?.incomeTaxNo);
    const companyTax = splitTaxNo(userDetails?.companyIncomeTaxNo);
    const setTax = (combinedKey, half) => (e) => {
        const next = half === 'type'
            ? combineTaxNo(e.target.value, splitTaxNo(userDetails?.[combinedKey]).no)
            : combineTaxNo(splitTaxNo(userDetails?.[combinedKey]).type, e.target.value);
        setUserDetails({ ...userDetails, [combinedKey]: next });
    };

    return (
        <div className="update-password-form-container">
            <form className="update-password-form" onSubmit={(e) => e.preventDefault()}>
                <Stack spacing={2}>
                    <Typography variant="h6">Update User Details</Typography>

                    {/* ── Personal Info ─────────────────────────────────── */}
                    <SectionHeader label="Personal Info" />

                    <TextField
                        fullWidth
                        label="Alias"
                        size="small"
                        placeholder="Alias"
                        value={userDetails?.alias || ''}
                        onChange={set('alias')}
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth required label="First Name (Given Name)" size="small"
                                value={userDetails?.firstName || ''} onChange={set('firstName')} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth required label="Last Name (Surname)" size="small"
                                value={userDetails?.lastName || ''} onChange={set('lastName')} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth required label="IC Number" size="small"
                                value={formatIc(userDetails?.icNumber)}
                                onChange={e => set('icNumber')({ target: { value: icInput(e.target.value) } })}
                                placeholder="e.g. 861002565414" />
                            <Box sx={{ mt: 1 }}>
                                <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                                    IC photocopy {icUrl ? '' : (simple ? '\u2014 required before we can pay you' : '\u2014 required before you can create a sale')}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Button component="label" size="small" variant="outlined" disabled={icBusy}
                                        sx={{ textTransform: 'none' }}>
                                        {icUrl ? 'Replace' : 'Upload IC'}
                                        <input hidden type="file" accept="image/*,application/pdf" onChange={onIcPick} />
                                    </Button>
                                    {icUrl && (
                                        <>
                                            <Button size="small" href={icUrl} target="_blank" rel="noreferrer"
                                                sx={{ textTransform: 'none' }}>View</Button>
                                            <Button size="small" color="error" onClick={onIcRemove} disabled={icBusy}
                                                sx={{ textTransform: 'none' }}>Remove</Button>
                                        </>
                                    )}
                                </Stack>
                                <Typography sx={{ fontSize: '0.68rem', color: icErr ? '#DC2626' : '#94A3B8', mt: 0.4 }}>
                                    {icErr || 'JPG, PNG, WEBP or PDF, up to 10MB.'}
                                </Typography>
                            </Box>
                        </Grid>
                        {!simple && (
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    select
                                    label="Tax Type"
                                    size="small"
                                    sx={{ minWidth: 90 }}
                                    value={personalTax.type}
                                    onChange={setTax('incomeTaxNo', 'type')}
                                >
                                    {TAX_TYPE_OPTIONS.map(opt => (
                                        <MenuItem key={opt || 'none'} value={opt}>{opt || '—'}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    fullWidth
                                    label="Income Tax No"
                                    size="small"
                                    placeholder="LHDN tax file digits"
                                    value={personalTax.no}
                                    onChange={setTax('incomeTaxNo', 'no')}
                                />
                            </Stack>
                        </Grid>
                        )}
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Email" type="email" size="small"
                                value={userDetails?.email || ''} onChange={set('email')} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Phone Number" type="tel" size="small"
                                value={userDetails?.phoneNumber || ''}
                                onChange={(e) => setUserDetails({ ...userDetails, phoneNumber: formatPhoneNumber(e.target.value) })} />
                        </Grid>
                    </Grid>

                    <TextField
                        fullWidth
                        label="Address"
                        size="small"
                        placeholder={simple ? 'Full mailing address' : 'Full mailing address (used on CP58)'}
                        value={userDetails?.address || ''}
                        onChange={set('address')}
                        multiline
                        minRows={2}
                    />

                    {/* ── Company Info ──────────────────────────────────── */}
                    {!simple && (
                    <>
                    <SectionHeader label="Company Info (if applicable)" />
                    <Box sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif", mt: -1 }}>
                        Fill these only if you operate as a registered company.
                        When set, CP58 + slip exports use the company info as the
                        recipient instead of your personal name + IC.
                    </Box>

                    <TextField
                        fullWidth
                        label="Company Name (Billing Name)"
                        size="small"
                        placeholder="e.g. ABC ENTERPRISE / SDN BHD"
                        value={userDetails?.billingName || ''}
                        onChange={set('billingName')}
                    />
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Company Reg No (SSM)"
                                size="small"
                                placeholder="e.g. 199301017370 / 272108-A"
                                value={userDetails?.companyRegNo || ''}
                                onChange={set('companyRegNo')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    select
                                    label="Tax Type"
                                    size="small"
                                    sx={{ minWidth: 90 }}
                                    value={companyTax.type}
                                    onChange={setTax('companyIncomeTaxNo', 'type')}
                                >
                                    {TAX_TYPE_OPTIONS.map(opt => (
                                        <MenuItem key={opt || 'none'} value={opt}>{opt || '—'}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    fullWidth
                                    label="Company Income Tax No"
                                    size="small"
                                    placeholder="e.g. 5875970000"
                                    value={companyTax.no}
                                    onChange={setTax('companyIncomeTaxNo', 'no')}
                                />
                            </Stack>
                        </Grid>
                    </Grid>

                    <TextField
                        fullWidth
                        label="Company Address"
                        size="small"
                        placeholder="Registered company address (used on CP58 in company mode)"
                        value={userDetails?.companyAddress || ''}
                        onChange={set('companyAddress')}
                        multiline
                        minRows={2}
                    />
                    </>
                    )}

                    {/* ── Bank Info ─────────────────────────────────────── */}
                    <SectionHeader label="Bank Info" />
                    <Box sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif", mt: -1 }}>
                        {simple
                            ? 'Where your referral fee is paid. The name should match the one above, or the bank will not accept the transfer.'
                            : `Used on the commission slip Maker CSV (bank-payment file).
                               Should match the company / personal name above so the
                               bank accepts the transfer.`}
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <BankSelect required label="Bank Name"
                                value={userDetails?.bankName || ''}
                                onChange={(v) => setUserDetails({ ...userDetails, bankName: v })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Bank Beneficiary Name" size="small"
                                helperText={simple
                                    ? 'Name on the bank account. Leave blank to use your full name'
                                    : 'Name on the bank account. Blank = company name, else your full name'}
                                value={userDetails?.bankBeneficiaryName || ''} onChange={set('bankBeneficiaryName')} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth required label="Bank Account Number" size="small"
                                value={userDetails?.bankAccountNumber || ''} onChange={set('bankAccountNumber')} />
                        </Grid>
                    </Grid>

                    <Stack direction="row" justifyContent="flex-end">
                        <Button
                            type="submit"
                            variant="contained"
                            onClick={handleUpdateUserProfile}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Stack>
                </Stack>
            </form>
        </div>
    );
}
